import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable
} from '@tanstack/react-table'
import {
  GetGrupoUsuariosDominioQueryQuery,
  GetGrupoUsuariosDominioQueryQueryVariables,
  GrupoUsuariosInput,
  Usuario,
  UsuariosByGruposUsuariosPagedQueryQuery
} from '@/core/types/graphql'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { checkbox } from '@tecsinapse/cortex-core'
import { t } from '@lingui/core/macro'
import { useQuery } from '@apollo/client'
import { rowsPerPageOptions } from '@/core/utils/rows-per-page'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { usuariosByGruposUsuariosPagedQueryGql } from '@/core/data/usuarios-by-gruposusuarios-paged'
import { GRUPO_USUARIOS_DOMINIO_QUERY } from '@/modules/grupo-usuarios-edicao/data/grupo-usuarios-dominio'
import { formatGroup } from '@/modules/grupo-usuarios-edicao/utils/functions'

export interface UseDialogByGruposUsuariosProps<T> {
  values?: T
  setValue?: UseFormSetValue<FieldValues>
  nameUsuariosRemovidos?: string
  idsGrupos?: string[]
  selection?: boolean
  idsUsuariosNaoSelecionados?: string[]
  isEdit?: boolean
}

export const useDialogByGruposUsuarios = ({
  values,
  setValue,
  idsGrupos,
  idsUsuariosNaoSelecionados,
  nameUsuariosRemovidos,
  selection,
  isEdit
}: UseDialogByGruposUsuariosProps<FieldValues>) => {
  const { data: { usuarioLogado } = {} } = useQuery<
    GetGrupoUsuariosDominioQueryQuery,
    GetGrupoUsuariosDominioQueryQueryVariables
  >(GRUPO_USUARIOS_DOMINIO_QUERY, {
    fetchPolicy: 'cache-first'
  })
  const grupo: GrupoUsuariosInput = values
    ? formatGroup({ grupoUsuarios: values, usuarioLogado })
    : null
  const columnHelper = createColumnHelper<Usuario>()
  const [totalCountWithoutSearchFilter, setTotalCountWithoutSearchFilter] =
    useState(0)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPageOptions[0]
  })

  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText] = useDebounce(searchText, 300)

  const gruposFilter = useMemo(
    () =>
      grupo
        ? {
            ...grupo,
            edit: isEdit
          }
        : (idsGrupos ?? []).map(id => ({
            id,
            todosDealers: false,
            active: true,
            nome: '',
            tipo: ''
          })),
    [grupo, idsGrupos, isEdit]
  )

  const filterInput = useMemo(
    () => ({
      grupos: gruposFilter,
      page: pagination.pageIndex,
      rowsPerPage: pagination.pageSize,
      busca: debouncedSearchText
    }),
    [gruposFilter, debouncedSearchText, pagination]
  )

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearchText])

  const { data, loading } = useQuery<UsuariosByGruposUsuariosPagedQueryQuery>(
    usuariosByGruposUsuariosPagedQueryGql,
    {
      variables: { filterInput },
      onCompleted: data => {
        if (filterInput.busca === '') {
          setTotalCountWithoutSearchFilter(
            data.usuariosByGruposUsuariosPaged?.totalElements ?? 0
          )
        }
      },
      fetchPolicy: 'no-cache'
    }
  )

  const usuarios = useMemo(
    () => (data?.usuariosByGruposUsuariosPaged?.content ?? []) as Usuario[],
    [data]
  )
  const totalElements = useMemo(
    () => data?.usuariosByGruposUsuariosPaged?.totalElements ?? 0,
    [data]
  )

  const handleCheckRow = useCallback(
    (id: string) => {
      const index = idsUsuariosNaoSelecionados?.findIndex(_id => _id === id)
      const updatedIdsUsuariosNaoSelecionados = [...idsUsuariosNaoSelecionados]
      if (index !== -1) {
        updatedIdsUsuariosNaoSelecionados.splice(index, 1)
      } else {
        updatedIdsUsuariosNaoSelecionados.push(id)
      }
      setValue?.(
        nameUsuariosRemovidos ?? '',
        updatedIdsUsuariosNaoSelecionados,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      )
    },
    [idsUsuariosNaoSelecionados, nameUsuariosRemovidos, setValue]
  )

  const columns = useMemo(() => {
    const selectionColumn = columnHelper.display({
      id: 'checkbox',
      cell: ({ row }) => {
        return (
          <input
            type='checkbox'
            className={checkbox()}
            checked={!idsUsuariosNaoSelecionados?.includes(row.original.id)}
            onChange={() => handleCheckRow(row.original.id)}
            onClick={e => e.stopPropagation()}
          />
        )
      }
    })

    const commonColumns = [
      columnHelper.accessor(row => row.nome ?? '-', {
        header: t`Nome`,
        cell: info => info.getValue(),
        enableSorting: true
      }),
      columnHelper.accessor(row => row.email ?? '-', {
        header: t`E-mail`,
        cell: info => info.getValue(),
        enableSorting: true
      })
    ]

    return selection ? [selectionColumn, ...commonColumns] : commonColumns
  }, [columnHelper, selection, idsUsuariosNaoSelecionados, handleCheckRow])

  const table = useReactTable({
    data: usuarios,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination },
    onPaginationChange: setPagination,
    rowCount: totalElements,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true
  })

  const rowsSelectedLength =
    totalCountWithoutSearchFilter - idsUsuariosNaoSelecionados?.length

  return {
    table,
    loading,
    rowsSelectedLength,
    setSearchText,
    totalElements
  }
}
